<template>
  <div class="tab-panel">
    <div class="house-buttons">
      <button type="button" @click="houseActive = '0'" :class="['house-btn', { active: houseActive === '0' }]">Resolutions</button>
      <button type="button" @click="houseActive = '1'" :class="['house-btn', { active: houseActive === '1' }]">Bills on First Reading</button>
      <button type="button" @click="houseActive = '2'" :class="['house-btn', { active: houseActive === '2' }]">Unnumbered Substitute Bill</button>
      <button type="button" @click="houseActive = '3'" :class="['house-btn', { active: houseActive === '3' }]">Committee Report / Substitute Bill</button>
      <button type="button" @click="houseActive = '4'" :class="['house-btn', { active: houseActive === '4' }]">Bills on Third Reading</button>
    </div>

    <div class="house-accordion">
      <!-- Resolutions -->
      <div v-show="houseActive === '0'" class="accordion-panel">
        <h4>Resolutions</h4>
        <div v-for="(resolution, num) in houseData.resolutions" :key="num" class="resolution-item">
          <div class="item-header">
            <h5>Resolution #{{ num + 1 }}</h5>
            <Button type="button" @click="deleteResolution(num)" :disabled="isSubButtonDisabled" :class="['delete-btn', { disabled: isSubButtonDisabled }]">Delete</Button>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>Resolution Title:</label>
              <InputText v-model="resolution.title"/>
            </div>
            <div class="form-group">
              <label>Date Filed:</label>
              <DatePicker v-model="resolution.datefiled" @date-select="dateSelect" />
            </div>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>Resolution No.:</label>
              <InputText v-model="resolution.no"/>
            </div>
            <div class="form-group">
              <label>Referred to Committee:</label>
              <MultiSelect v-model="resolution.committee" :options="referrals" placeholder="Select Committee" class="w-full" filter />
            </div>
            <div class="form-group">
              <label>Author(s):</label>
              <MultiSelect v-model="resolution.authors" :options="authors" placeholder="Select Author" class="w-full" filter />
            </div>
          </div>
          <div class="form-group">
            <label>Remarks:</label>
            <Textarea v-model="resolution.remarks" rows="2"/>
          </div>
          <div class="form-group">
            <label>Salient Features:</label>
            <Textarea v-model="resolution.salient_features" rows="2" placeholder="Enter salient features..."/>
          </div>

          <!-- Legislative Status Section -->
          <div class="subsection">
            <div class="subsection-header">
              <h6>Legislative Status</h6>
              <Button type="button" @click="addLegisStatus(num)" :disabled="isSubButtonDisabled" :class="['add-sub-btn', { disabled: isSubButtonDisabled }]">Add Legislative Status</Button>
            </div>
            <div v-for="(status, sidx) in resolution.legisstatus" :key="sidx" class="status-item">
              <div class="status-header">
                <span>Status #{{ sidx + 1 }}</span>
                <Button type="button" @click="deleteLegisStatus(num, sidx)" :disabled="isSubButtonDisabled" :class="['delete-sub-btn', { disabled: isSubButtonDisabled }]">Delete</Button>
              </div>
              <div class="form-grid">
                <div class="form-group">
                  <label>Status:</label>
                  <Select v-model="status.status" :options="statusResolutions" />
                </div>
                <div class="form-group">
                  <label>Date:</label>
                  <DatePicker v-model="status.date" @date-select="dateSelect"  />
                </div>
              </div>
            </div>
          </div>

        </div>

        <Button type="button" @click="addResolution()" :disabled="isSubButtonDisabled" :class="['add-btn', { disabled: isSubButtonDisabled }]">Add Resolution</Button>
        
        <!-- Position Papers Section for Resolutions -->
        <PositionPaper 
          :positionPapers="houseResolutionsPositionPapers"
          :isDisabled="isSubButtonDisabled"
          :statusOptions="statusResolutions"
          @addPositionPaper="() => $emit('addPositionPaper', 'house', 'resolution')"
          @deletePositionPaper="(index) => $emit('deletePositionPaper', 'house', 'resolution', index)"
        />
      </div>

      <!-- Bills on First Reading -->
      <div v-show="houseActive === '1'" class="accordion-panel">
        <h4>Bills on First Reading</h4>
        <div v-for="(bill, num) in houseData.firstreading" :key="num" class="bill-item">
          <div class="item-header">
            <h5>Bill #{{ num + 1 }}</h5>
            <Button type="button" @click="deleteFirstReading(num)" :disabled="isSubButtonDisabled" :class="['delete-btn', { disabled: isSubButtonDisabled }]">Delete</Button>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>Bill Title:</label>
              <InputText v-model="bill.title" />
            </div>
            <div class="form-group">
              <label>Short Title:</label>
              <InputText v-model="bill.shorttitle" />
            </div>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>HB No.:</label>
              <InputText v-model="bill.no" />
            </div>
            <div class="form-group">
              <label>Author(s):</label>
              <MultiSelect v-model="bill.authors" :options="authors" placeholder="Select Author" class="w-full" filter />
            </div>
            <div class="form-group">
              <label>Date Filed:</label>
              <DatePicker v-model="bill.datefiled" @date-select="dateSelect"  />
            </div>
            <div class="form-group">
              <label>Referred to Committee:</label>
              <MultiSelect v-model="bill.committee" :options="referrals" placeholder="Select Committee" class="w-full" filter />
            </div>
          </div>
          <div class="form-group">
            <label>Remarks:</label>
            <Textarea v-model="bill.remarks" rows="2"></Textarea>
          </div>
          <div class="form-group">
            <label>Source/Link:</label>
            <Textarea v-model="bill.source" rows="2"></Textarea>
          </div>
          <div class="form-group">
            <label>Salient Features:</label>
            <Textarea v-model="bill.salient_features" rows="2" placeholder="Enter salient features..."></Textarea>
          </div>

          <!-- Legislative Status Section -->
          <div class="subsection">
            <div class="subsection-header">
              <h6>Legislative Status</h6>
              <Button type="button" @click="addFirstReadingLegisStatus(num)" :disabled="isSubButtonDisabled" :class="['add-sub-btn', { disabled: isSubButtonDisabled }]">Add Legislative Status</Button>
            </div>
            <div v-for="(status, sidx) in bill.legisstatus" :key="sidx" class="status-item">
              <div class="status-header">
                <span>Status #{{ sidx + 1 }}</span>
                <Button type="button" @click="deleteFirstReadingLegisStatus(num, sidx)" :disabled="isSubButtonDisabled" :class="['delete-sub-btn', { disabled: isSubButtonDisabled }]">Delete</Button>
              </div>
              <div class="form-grid">
                <div class="form-group">
                  <label>Status:</label>
                  <Select v-model="status.status" :options="statusFirstReading" />
                </div>
                <div class="form-group">
                  <label>Date:</label>
                  <DatePicker v-model="status.date" @date-select="dateSelect"  />
                </div>
              </div>
            </div>
          </div>


        </div>
        <Button type="button" @click="addFirstReading()" :disabled="isSubButtonDisabled" :class="['add-btn', { disabled: isSubButtonDisabled }]">Add Bill on First Reading</Button>
        
        <!-- Position Papers Section for First Reading -->
        <PositionPaper 
          :positionPapers="houseFirstReadingPositionPapers"
          :isDisabled="isSubButtonDisabled"
          :statusOptions="statusFirstReading"
          @addPositionPaper="() => $emit('addPositionPaper', 'house', 'first_reading')"
          @deletePositionPaper="(index) => $emit('deletePositionPaper', 'house', 'first_reading', index)"
        />
      </div>

      <!-- Unnumbered Substitute Bill -->
      <div v-show="houseActive === '2'" class="accordion-panel">
        <h4>Unnumbered Substitute Bill</h4>
        <div v-for="(bill, num) in houseData.unnumbered" :key="num" class="bill-item">
          <div class="item-header">
            <h5>Bill #{{ num + 1 }}</h5>
            <Button type="button" @click="deleteUnnumbered(num)" :disabled="isSubButtonDisabled" :class="['delete-btn', { disabled: isSubButtonDisabled }]">Delete</Button>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>Bill Title:</label>
              <InputText v-model="bill.title" />
            </div>
            <div class="form-group">
              <label>Short Title:</label>
              <InputText v-model="bill.shorttitle" />
            </div>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>Substituting Bills:</label>
              <InputText v-model="bill.substituting" />
            </div>
            <div class="form-group">
              <label>Author(s):</label>
              <MultiSelect v-model="bill.authors" :options="authors" placeholder="Select Author" class="w-full" filter />
            </div>
          </div>
          <div class="form-group">
            <label>Remarks:</label>
            <Textarea v-model="bill.remarks" rows="2"></Textarea>
          </div>
          <div class="form-group">
            <label>Source/Link:</label>
            <Textarea v-model="bill.source" rows="2"></Textarea>
          </div>
          <div class="form-group">
            <label>Salient Features:</label>
            <Textarea v-model="bill.salient_features" rows="2" placeholder="Enter salient features..."></Textarea>
          </div>

          <!-- Legislative Status Section -->
          <div class="subsection">
            <div class="subsection-header">
              <h6>Legislative Status</h6>
              <Button type="button" @click="addUnnumberedLegisStatus(num)" :disabled="isSubButtonDisabled" :class="['add-sub-btn', { disabled: isSubButtonDisabled }]">Add Legislative Status</Button>
            </div>
            <div v-for="(status, sidx) in bill.legisstatus" :key="sidx" class="status-item">
              <div class="status-header">
                <span>Status #{{ sidx + 1 }}</span>
                <Button type="button" @click="deleteUnnumberedLegisStatus(num, sidx)" :disabled="isSubButtonDisabled" :class="['delete-sub-btn', { disabled: isSubButtonDisabled }]">Delete</Button>
              </div>
              <div class="form-grid">
                <div class="form-group">
                  <label>Status:</label>
                  <Select v-model="status.status " :options="statusFirstReading" />
                </div>
                <div class="form-group">
                  <label>Date:</label>
                  <DatePicker v-model="status.date" @date-select="dateSelect" />
                </div>
              </div>
            </div>
          </div>

        </div>
        <Button type="button" @click="addUnnumbered()" :disabled="isSubButtonDisabled" :class="['add-btn', { disabled: isSubButtonDisabled }]">Add Unnumbered Substitute Bill</Button>

        <!-- Position Papers Section for Unnumbered Bills -->
        <PositionPaper 
          :positionPapers="houseUnnumberedPositionPapers"
          :isDisabled="isSubButtonDisabled"
          :statusOptions="statusFirstReading"
          @addPositionPaper="() => $emit('addPositionPaper', 'house', 'unnumbered')"
          @deletePositionPaper="(index) => $emit('deletePositionPaper', 'house', 'unnumbered', index)"
        />
      </div>

      <!-- Committee Report / Substitute Bill -->
      <div v-show="houseActive === '3'" class="accordion-panel">
        <h4>Committee Report / Substitute Bill</h4>
        <div class="bill-item">
          <div class="form-grid">
            <div class="form-group">
              <label>Bill Title:</label>
              <InputText v-model="houseData.committee.billtitle" />
            </div>
            <div class="form-group">
              <label>Short Title:</label>
              <InputText v-model="houseData.committee.shorttitle" />
            </div>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>HB No. / Committee Report:</label>
              <InputText v-model="houseData.committee.no" />
            </div>
            <div class="form-group">
              <label>Author(s):</label>
              <MultiSelect v-model="houseData.committee.authors" :options="authors" placeholder="Select Author" class="w-full" filter />
            </div>
          </div>
          <div class="form-group">
            <label>Remarks:</label>
            <Textarea v-model="houseData.committee.remarks" rows="2"></Textarea>
          </div>
          <div class="form-group">
            <label>Source/Link:</label>
            <Textarea v-model="houseData.committee.source" rows="2"></Textarea>
          </div>
          <div class="form-group">
            <label>Salient Features:</label>
            <Textarea v-model="houseData.committee.salient_features" rows="2" placeholder="Enter salient features..."></Textarea>
          </div>

          <!-- Legislative Status Section -->
          <div class="subsection">
            <div class="subsection-header">
              <h6>Legislative Status</h6>
              <Button type="button" @click="addCommitteeLegisStatus()" :disabled="isSubButtonDisabled" :class="['add-sub-btn', { disabled: isSubButtonDisabled }]">Add Legislative Status</Button>
            </div>
            <div v-for="(status, sidx) in houseData.committee.legisstatus" :key="sidx" class="status-item">
              <div class="status-header">
                <span>Status #{{ sidx + 1 }}</span>
                <Button type="button" @click="deleteCommitteeLegisStatus(sidx)" :disabled="isSubButtonDisabled" :class="['delete-sub-btn', { disabled: isSubButtonDisabled }]">Delete</Button>
              </div>
              <div class="form-grid">
                <div class="form-group">
                  <label>Status:</label>
                  <Select v-model="status.status" :options="statusCommitteeReport" />
                </div>
                <div class="form-group">
                  <label>Date:</label>
                  <DatePicker v-model="status.date" @date-select="dateSelect"  />
                </div>
              </div>
            </div>
          </div>

        </div>
        
        <!-- Position Papers Section for Committee Reports -->
        <PositionPaper 
          :positionPapers="houseCommitteePositionPapers"
          :isDisabled="isSubButtonDisabled"
          :statusOptions="statusCommitteeReport"
          @addPositionPaper="() => $emit('addPositionPaper', 'house', 'committee_report')"
          @deletePositionPaper="(index) => $emit('deletePositionPaper', 'house', 'committee_report', index)"
        />
      </div>

      <!-- Bills on Third Reading -->
      <div v-show="houseActive === '4'" class="accordion-panel">
        <h4>Bills on Third Reading</h4>
        <div class="bill-item">
          <div class="form-grid">
            <div class="form-group">
              <label>Bill Title:</label>
              <InputText v-model="houseData.thirdreading.billtitle" />
            </div>
            <div class="form-group">
              <label>Short Title:</label>
              <InputText v-model="houseData.thirdreading.shorttitle" />
            </div>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>HB No. / Committee Report:</label>
              <InputText v-model="houseData.thirdreading.no" />
            </div>
            <div class="form-group">
              <label>Author(s):</label>
              <MultiSelect v-model="houseData.thirdreading.authors" :options="authors" placeholder="Select Author" class="w-full" filter />
            </div>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>Remarks:</label>
              <Textarea v-model="houseData.thirdreading.remarks" rows="2"></Textarea>
            </div>
            <div class="form-group">
              <label>Source/Link:</label>
              <Textarea v-model="houseData.thirdreading.source" rows="2"></Textarea>
            </div>
          </div>
          <div class="form-group">
            <label>Salient Features:</label>
            <Textarea v-model="houseData.thirdreading.salient_features" rows="2" placeholder="Enter salient features..."></Textarea>
          </div>

          <!-- Legislative Status Section -->
          <div class="subsection">
            <div class="subsection-header">
              <h6>Legislative Status</h6>
              <Button type="button" @click="addThirdReadingLegisStatus()" :disabled="isSubButtonDisabled" :class="['add-sub-btn', { disabled: isSubButtonDisabled }]">Add Legislative Status</Button>
            </div>
            <div v-for="(status, sidx) in houseData.thirdreading.legisstatus" :key="sidx" class="status-item">
              <div class="status-header">
                <span>Status #{{ sidx + 1 }}</span>
                <Button type="button" @click="deleteThirdReadingLegisStatus(sidx)" :disabled="isSubButtonDisabled" :class="['delete-sub-btn', { disabled: isSubButtonDisabled }]">Delete</Button>
              </div>
              <div class="form-grid">
                <div class="form-group">
                  <label>Status:</label>
                  <Select v-model="status.status" :options="statusThirdReading" />
                </div>
                <div class="form-group">
                  <label>Date:</label>
                  <DatePicker v-model="status.date" @date-select="dateSelect"  />
                </div>
              </div>
            </div>
          </div>

        </div>
        
        <!-- Position Papers Section for Third Reading -->
        <PositionPaper 
          :positionPapers="houseThirdReadingPositionPapers"
          :isDisabled="isSubButtonDisabled"
          :statusOptions="statusThirdReading"
          @addPositionPaper="() => $emit('addPositionPaper', 'house', 'third_reading')"
          @deletePositionPaper="(index) => $emit('deletePositionPaper', 'house', 'third_reading', index)"
        />
      </div>
    </div>

    <!-- Key Issues Section -->
    <KeyIssuesSection 
      :keyIssues="keyIssues"
      :activeTab="keyIssuesActive"
      :revenueStartYear="revenueStartYear"
      :revenueEndYear="revenueEndYear"
      :budgetaryStartYear="budgetaryStartYear"
      :budgetaryEndYear="budgetaryEndYear"
      @update:activeTab="keyIssuesActive = $event"
      @update:keyIssues="$emit('update:keyIssues', $event)"
      @addRevenueRow="$emit('addRevenueRow')"
      @addBudgetaryRow="$emit('addBudgetaryRow')"
      @addDofConcernsRow="$emit('addDofConcernsRow')"
      @deleteRevenueRow="$emit('deleteRevenueRow', $event)"
      @deleteBudgetaryRow="$emit('deleteBudgetaryRow', $event)"
      @deleteDofConcernsRow="$emit('deleteDofConcernsRow', $event)"
      @getYearTotal="$emit('getYearTotal', $event)"
      @autoResize="$emit('autoResize', $event)"
      @updateRevenueStartYear="$emit('updateRevenueStartYear', $event)"
      @updateRevenueEndYear="$emit('updateRevenueEndYear', $event)"
      @updateBudgetaryStartYear="$emit('updateBudgetaryStartYear', $event)"
      @updateBudgetaryEndYear="$emit('updateBudgetaryEndYear', $event)"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import MultiSelect from 'primevue/multiselect'
import KeyIssuesSection from './KeyIssuesSection.vue'
import PositionPaper from './PositionPaper.vue'
import moment from 'moment-timezone';

moment.tz.setDefault('Asia/Taipei')
Date.prototype.toJSON = function() {
  return moment(this).tz("Asia/Taipei").format();
};

const dateSelect = () => {
  return moment().tz("Asia/Taipei").format();
  //Date.prototype.toJSON = function(){ return moment(this).format(); }
  //moment().tz("Asia/Taipei").format();
  //return new Date().toJSON();
}

const props = defineProps({
  houseData: {
    type: Object,
    required: true
  },
  keyIssues: {
    type: Object,
    required: true
  },
  referrals: {
    type: Array,
    default: () => []
  },
  authors: {
    type: Array,
    default: () => []
  },
  isSubButtonDisabled: {
    type: Boolean,
    default: false
  },
  revenueStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  revenueEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  budgetaryStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  budgetaryEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  positionPapers: {
    type: Array,
    default: () => []
  },
  houseActiveTab: {
    type: String,
    default: '0'
  }
})

const emit = defineEmits([
  'trackDeletedId',
  'addRevenueRow',
  'addBudgetaryRow', 
  'addDofConcernsRow',
  'deleteRevenueRow',
  'deleteBudgetaryRow',
  'deleteDofConcernsRow',
  'getYearTotal',
  'autoResize',
  'addPositionPaper',
  'deletePositionPaper',
  'update:keyIssues',
  'updateRevenueStartYear',
  'updateRevenueEndYear',
  'updateBudgetaryStartYear',
  'updateBudgetaryEndYear'
])

// Computed properties for filtered position papers
const houseResolutionsPositionPapers = computed(() => 
  props.positionPapers.filter(p => p.type === 'house' && p.sub_type === 'resolution')
)
const houseFirstReadingPositionPapers = computed(() => 
  props.positionPapers.filter(p => p.type === 'house' && p.sub_type === 'first_reading')
)
const houseUnnumberedPositionPapers = computed(() => 
  props.positionPapers.filter(p => p.type === 'house' && p.sub_type === 'unnumbered')
)
const houseCommitteePositionPapers = computed(() => 
  props.positionPapers.filter(p => p.type === 'house' && p.sub_type === 'committee_report')
)
const houseThirdReadingPositionPapers = computed(() => 
  props.positionPapers.filter(p => p.type === 'house' && p.sub_type === 'third_reading')
)

const houseActive = ref(props.houseActiveTab)
const keyIssuesActive = ref('revenue')

watch(() => props.houseActiveTab, (newValue) => {
  houseActive.value = newValue
})

const statusResolutions = [
  'Briefing',
  '1st Public Hearing',
  '2nd Public Hearing',
  '3rd Public Hearing',
  'TWG',
  'Approved/Adopted Resolutions'
];

const statusFirstReading = [
  '1st Briefing',
  '2nd Briefing',
  '3rd Briefing',
  '1st Public Hearing',
  '2nd Public Hearing',
  '3rd Public Hearing',
  'TWG',
  'Joint Committee Meeting',
  'Referral',
  'Approved with Ways and Means',
  'Approved with Appropriations'
];

const statusCommitteeReport = [
  'Period of Sponsorship',
  'Period of Interpellations',
  'Period of Amendments',
  'Approved on Second Reading'
];

const statusThirdReading = [
  'Approval on Third Reading',
  'Transmitted to Senate', 
];

// Resolution methods
const addResolution = () => {
  props.houseData.resolutions.push({
    title: '', datefiled: '', no: '', committee: '', authors: '', remarks: '',
    type: 'house',
    legisstatus: [],
    positionpapers: []
  })
}

const deleteResolution = (index) => {
  const paper = props.houseData.resolutions[index]
  emit('trackDeletedId', 'bills', paper.id)
  props.houseData.resolutions.splice(index, 1)
}

const addLegisStatus = (resolutionIndex) => {
  props.houseData.resolutions[resolutionIndex].legisstatus.push({
    status: '', date: ''
  })
}

const deleteLegisStatus = (resolutionIndex, statusIndex) => {
  const status = props.houseData.resolutions[resolutionIndex].legisstatus[statusIndex]
  emit('trackDeletedId', 'legislative_status', status.id)
  props.houseData.resolutions[resolutionIndex].legisstatus.splice(statusIndex, 1)
}

// First Reading methods
const addFirstReading = () => {
  props.houseData.firstreading.push({
    title: '', shorttitle: '', no: '', authors: '', datefiled: '', committee: '', remarks: '', source: '',
    type: 'house',
    legisstatus: [],
    positionpapers: []
  })
}


const deleteFirstReading = (index) => {
  const paper = props.houseData.firstreading[index];
  emit('trackDeletedId', 'bills', paper.id)
  props.houseData.firstreading.splice(index, 1)
}

const addFirstReadingLegisStatus = (billIndex) => {
  props.houseData.firstreading[billIndex].legisstatus.push({
    status: '', date: ''
  })
}

const deleteFirstReadingLegisStatus = (billIndex, statusIndex) => {
  const status = props.houseData.firstreading[billIndex].legisstatus[statusIndex]
  emit('trackDeletedId', 'legislative_status', status.id)
  props.houseData.firstreading[billIndex].legisstatus.splice(statusIndex, 1)
}

// Unnumbered methods
const addUnnumbered = () => {
  props.houseData.unnumbered.push({
    title: '', shorttitle: '', substituting: '', authors: '', remarks: '', source: '',
    type: 'house',
    legisstatus: [],
    positionpapers: []
  })
}

const deleteUnnumbered = (index) => {
  const paper = props.houseData.unnumbered[index]
  emit('trackDeletedId', 'biils', paper.id)
  props.houseData.unnumbered.splice(index, 1)
}

const addUnnumberedLegisStatus = (billIndex) => {
  props.houseData.unnumbered[billIndex].legisstatus.push({
    status: '', date: ''
  })
}

const deleteUnnumberedLegisStatus = (billIndex, statusIndex) => {
  const status = props.houseData.unnumbered[billIndex].legisstatus[statusIndex]
  emit('trackDeletedId', 'legislative_status', status.id)
  props.houseData.unnumbered[billIndex].legisstatus.splice(statusIndex, 1)
}

// Committee methods
const addCommitteeLegisStatus = () => {
  if (!props.houseData.committee.legisstatus) {
    props.houseData.committee.legisstatus = []
  }
  props.houseData.committee.legisstatus.push({
    status: '', date: ''
  })
}

const deleteCommitteeLegisStatus = (statusIndex) => {
  const status = props.houseData.committee.legisstatus[statusIndex]
  emit('trackDeletedId', 'legislative_status', status.id)
  props.houseData.committee.legisstatus.splice(statusIndex, 1)
}


// Third Reading methods
const addThirdReadingLegisStatus = () => {
  if (!props.houseData.thirdreading.legisstatus) {
    props.houseData.thirdreading.legisstatus = []
  }
  props.houseData.thirdreading.legisstatus.push({
    status: '', date: ''
  })
}

const deleteThirdReadingLegisStatus = (statusIndex) => {
  const status = props.houseData.thirdreading.legisstatus[statusIndex]
  emit('trackDeletedId', 'legislative_status', status.id)
  props.houseData.thirdreading.legisstatus.splice(statusIndex, 1)
}

</script>

<style scoped>
.house-buttons {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.house-btn {
  background: #f3f4f6;
  border: 2px solid #e5e7eb;
  color: #374151;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.85rem;
}

.house-btn:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
}

.house-btn.active {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  border-color: #1e3a8a;
  box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
}

.accordion-panel {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid #e5e7eb;
  margin-bottom: 1rem;
}

.accordion-panel h4 {
  color: #1e3a8a;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #fbbf24;
}

.resolution-item, .bill-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.item-header h5 {
  color: #1e3a8a;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #1e3a8a;
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #1e3a8a;
  box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1);
}

.subsection {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.subsection-header h6 {
  color: #1e3a8a;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
}

.status-item, .position-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.status-header, .position-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.add-btn, .add-sub-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.add-btn:hover, .add-sub-btn:hover {
  background: linear-gradient(135deg, #047857 0%, #10b981 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.delete-btn, .delete-sub-btn {
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.8rem;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.delete-btn:hover, .delete-sub-btn:hover {
  background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
}

.delete-btn.disabled, .delete-sub-btn.disabled,
.add-btn.disabled, .add-sub-btn.disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.delete-btn.disabled:hover, .delete-sub-btn.disabled:hover,
.add-btn.disabled:hover, .add-sub-btn.disabled:hover {
  background: #9ca3af;
  transform: none;
  box-shadow: none;
}

@media (max-width: 768px) {
  .house-buttons {
    flex-direction: column;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .subsection-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .status-header, .position-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>